import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import _ from 'lodash';
import { useResizeDetector } from 'react-resize-detector';
import { Button, Flex, FlexItem, Stack, StackItem, Banner, CodeBlock } from '@patternfly/react-core';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import Editor from '@monaco-editor/react';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';

import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import T from 'localization/LocalizationKeys';

import ModelType from 'components/Model/ModelType';
import ModelsContainer from 'components/Model/ModelsContainer';
import { hasValidInput } from 'components/Model/ModelUtils';
import { getConfig } from 'config';
import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import { getComponents, getProperties, parse } from 'utils/dtdl';
import DeleteProvisionTemplate from './DeleteProvisionTemplate';
import Tags, { parseTagsFilterListToTagObjects, toTagObjects } from 'components/Tags/tags';
import ViewModal from 'components/Modals/ViewModal';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';

const iotDeviceTemplate = {
  iotDeviceTemplateOptions: {
    desiredProperties: {
    }
  }
};

const edgeDeviceTemplate = {
  edgeDeviceTemplateOptions: {
    manifest: {
    },
    deviceDesiredProperties: {
    },
    modulesDesiredProperties: {
      module1: {
      }
    }
  }
};

const optionTrueFalse = [
  { text: 'True', value: true },
  { text: 'False', value: false }
];

const templateModel = [
  {
    type: ModelType.TEXT,
    path: 'id',
    label: 'Id',
    isReadOnly: true
  },
  {
    type: ModelType.TEXT,
    path: 'displayName',
    label: T.common.displayname,
    isRequired: true,
    hasError: _.isEmpty
  }
];

const templateDescriptionModel = [
  {
    type: ModelType.TEXTAREA,
    path: 'description',
    label: T.common.description
  }
];
const createTemplateQuery = (api, templateId) => ({
  queryKey: ['template', templateId],
  queryFn: createQueryRequest(api.core.getProviderClientTemplate)
});

export const TemplateName = ({ templateId }) => {
  const api = useAPI();
  const templateQuery = useQuery(createTemplateQuery(api, templateId));
  return templateQuery.isLoading ? <BreadcrumbSkeleton /> : (templateQuery.data?.displayName ?? '');
};

const AdminProvisionTemplatePage = ({ breadcrumbs }) => {
  const monaco = useRef();
  const api = useAPI();
  const { modelId, templateId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { height, ref } = useResizeDetector();
  const [template, setTemplate] = useState({});
  const envConfig = getConfig();
  const [, setProperties] = useState([]);
  const [, setComponents] = useState([]);
  const [options, setOptions] = useState({});
  const [banner, setBanner] = useState('green');
  const [bannerMessage, setBannerMessage] = useState('');
  const [viewErrorMessage, setViewErrorMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [config, setConfig] = useState({
    hub: envConfig.providerData.iothubService.hubName,
    isEdge: _.find(optionTrueFalse, option => option.value === false)
  });
  const tagFilter = useMemo(() => {
    const query = queryString.parse(location.search, { arrayFormat: 'comma' });
    return _.flatten([query?.tagFilter ?? []]);
  }, [location]);

  const [tags, setTags] = useState(
    parseTagsFilterListToTagObjects(tagFilter)
  );

  const readonly = useMemo(() => {
    const query = queryString.parse(location.search);
    return query?.readonly === 'true';
  }, [location]);

  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const deletedTemplate = useCallback(() => {
    setConfirmDeleteItem(null);
    history.goBack();
  }, [history]);

  const [createTemplate, isCreatingTemplate] = useApiCall({
    apiCall: api.core.createProviderClientTemplate,
    onSuccess: useCallback((template) => {
      history.replace({ ...location, pathname: location.pathname.replace('new-template', template.id) });
    }, [history, location]),
    onError: useCallback((error) => {
      console.error(error);
    }, [])
  });

  const [updateTemplate, isUpdatingTemplate] = useApiCall({
    apiCall: api.core.updateProviderClientTemplate,
    onSuccess: useCallback(() => {
    }, []),
    onError: useCallback((error) => {
      console.error(error);
    }, [])
  });

  useQuery(['template', templateId],
    createQueryRequest(api.core.getProviderClientTemplate),
    { enabled: templateId != null,
      onSuccess: (template) => {
        setTemplate(template);
        const removeConfig = _.omit(template?.options, ['hub', 'isEdge']);
        setOptions(_.isEmpty(removeConfig) ? iotDeviceTemplate : removeConfig);
        setConfig({
          hub: template?.options?.hub ?? envConfig.providerData.iothubService.hubName,
          isEdge: _.find(optionTrueFalse, option => template?.options?.isEdge === option.value)
        });
        setTags(toTagObjects(template?.tags));
      }
    });

  useQuery(['model', modelId],
    createQueryRequest(api.core.getModel),
    { enabled: modelId != null,
      onSuccess: (data) => {
        const m = parse(data.modelData[0], data.modelData);
        setComponents(getComponents({ model: m, pruneRoot: true }));
        setProperties(getProperties({ dtdlModel: m }));
      }
    });

  useEffect(function onEdgeChange() {
    if (config.isEdge?.value) {
      setOptions(edgeDeviceTemplate);
    } else {
      setOptions(iotDeviceTemplate);
    }
  }, [config.isEdge]);

  const handleEditorValidation = useCallback((markers) => {
    if (_.isEmpty(markers)) {
      setBanner('green');
      setBannerMessage('');
    } else {
      setBanner('red');
      setBannerMessage('Invalid JSON, click to view more details.');
      setErrorMessage(<CodeBlock>{ _.map(markers, marker => `Line ${marker.endLineNumber} : ${marker.message}`) }</CodeBlock>);
    }
  }, []);

  const configModel = useMemo(() => {
    return [
      {
        type: ModelType.TEXT,
        path: 'hub',
        label: 'Iot Hub',
        isReadOnly: true
      },
      {
        type: ModelType.OPTIONSV2,
        path: 'isEdge',
        label: 'Is Edge',
        isRequired: true,
        // disabled: _.isEmpty(components),
        options: _.map(optionTrueFalse, (option) => ({ value: option, text: option.text }))
      }
    ];
  }, []);

  const onDelete = useCallback(() => {
    setConfirmDeleteItem(template);
  }, [template]);

  const onSave = useCallback(() => {
    const t = _.reduce(tags, (acc, tag) => {
      if (!acc[tag.name]) {
        acc[tag.name] = [];
      }
      acc[tag.name].push(tag.value);
      return acc;
    }, {});
    const editorData = JSON.parse(monaco.current.getValue());
    const templ = {
      ...template,
      tags: t,
      options: {
        ...editorData,
        hub: config.hub,
        isEdge: config.isEdge.value
      }
    };
    setOptions(editorData);
    if (_.isEmpty(templateId)) {
      createTemplate(templ);
    } else {
      updateTemplate(templateId, templ);
    }
  }, [config, createTemplate, tags, template, templateId, updateTemplate]);

  const onBannerClick = useCallback(() => {
    setViewErrorMessage(errorMessage);
  }, [errorMessage]);

  const handleEditorDidMount = useCallback((editor) => {
    monaco.current = editor;
  }, []);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/provision-templates/edit'}>
      <Stack>
        <StackItem>
          <Flex>
            <FlexItem>
              <ModelsContainer models={templateModel} data={template} setData={setTemplate} />
            </FlexItem>
            <FlexItem>
              <ModelsContainer models={templateDescriptionModel} data={template} setData={setTemplate} />
            </FlexItem>
            <FlexItem>
              <ModelsContainer models={configModel} data={config} setData={setConfig} />
            </FlexItem>
            <FlexItem>
              <Flex direction={{ default: 'column' }} >
                <FlexItem>
                  <Button isBlock isLoading={isCreatingTemplate || isUpdatingTemplate} onClick={onSave} isDisabled={readonly || !hasValidInput(template, templateModel)}>save</Button>
                </FlexItem>
                <FlexItem>
                  <Button variant="danger" onClick={onDelete} isDisabled={readonly || _.isEmpty(templateId)}>Delete</Button>
                </FlexItem>
              </Flex>
            </FlexItem>
          </Flex>
        </StackItem>
        <StackItem>
          <Tags tags={tags} setTags={setTags}/>
        </StackItem>
        <StackItem>
          <Banner style={{ height: '30px' }} variant={banner} onClick={() => onBannerClick()}>
            { bannerMessage }
          </Banner>
        </StackItem>
        <StackItem isFilled>
          <div ref={ref} style={{ height: '100%' }}>
            <Editor
              theme="vs-dark"
              height={height ? height - 50 : undefined}
              defaultLanguage="json"
              onValidate={handleEditorValidation}
              value={JSON.stringify(options, null, 2)}
              onMount={handleEditorDidMount}
            />
          </div>
        </StackItem>
      </Stack>
      <DeleteProvisionTemplate
        confirmDeleteItem={confirmDeleteItem}
        onDelete={deletedTemplate}
        cancelDelete={cancelDelete}
      />
      <ViewModal
        title="View Error Message"
        onClose={() => setViewErrorMessage(null)}
        Component={viewErrorMessage}
      />
    </ContentPage>
  );
};

export default AdminProvisionTemplatePage;
